import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { UserGetWrongResponse } from '../types/user'

// 오답 문제 타입
type WrongQuestion = UserGetWrongResponse['data'][0]

interface WrongUserAnswer {
  questionId: number
  selectedAnswerIndex: number
  isCorrect: boolean
  timestamp: Date
}

interface WrongQuizState {
  // 기본 정보
  userId: number | null
  userStep: number | null

  // 진행 상태
  currentQuestionIndex: number

  // 문제 및 답안 데이터
  wrongQuestions: WrongQuestion[]
  userAnswers: WrongUserAnswer[]

  // UI 상태
  showModal: boolean
  modalData: {
    isCorrect: boolean
    explanation: string
    correctAnswer: string
  } | null
}

interface WrongQuizActions {
  // 초기화 - ✅ userStep 파라미터 추가
  initializeWrongQuiz: (
    userId: number,
    userStep: number,
    questions: WrongQuestion[]
  ) => void
  reset: () => void

  // 퀴즈 진행
  goToNextQuestion: () => void
  goToPreviousQuestion: () => void
  setCurrentQuestionIndex: (index: number) => void
  submitAnswer: (
    questionId: number,
    answerIndex: number,
    isCorrect: boolean
  ) => void

  // 모달 관리
  showAnswerModal: (data: {
    isCorrect: boolean
    explanation: string
    correctAnswer: string
  }) => void
  closeModal: () => void

  // 데이터 조회
  getCurrentQuestion: () => WrongQuestion | null
  isLastQuestion: () => boolean
  getUserAnswers: () => WrongUserAnswer[]
}

export const useWrongQuizStore = create<WrongQuizState & WrongQuizActions>()(
  persist(
    (set, get) => ({
      userId: null,
      userStep: null, // ✅ 초기값 추가
      currentQuestionIndex: 0,
      wrongQuestions: [],
      userAnswers: [],
      showModal: false,
      modalData: null,

      initializeWrongQuiz: (userId, userStep, questions) => {
        const {
          userAnswers: existingAnswers,
          userId: existingUserId,
          userStep: existingStep,
        } = get()

        const isSame = existingUserId === userId && existingStep === userStep

        set({
          userId,
          userStep,
          wrongQuestions: questions,
          currentQuestionIndex: 0,
          userAnswers: isSame ? existingAnswers : [],
          showModal: false,
          modalData: null,
        })

        // 🔥 같은 유저 & 같은 STEP일 때만 답변 유지
        // const validAnswers =
        //   existingUserId === userId && existingStep === userStep
        //     ? existingAnswers.filter((answer) =>
        //         questions.some((q) => q.QUESTION_ID === answer.questionId)
        //       )
        //     : []

        // console.log('🔄 오답 퀴즈 초기화:', {
        //   userId,
        //   userStep,
        //   isSameUser: existingUserId === userId,
        //   isSameStep: existingStep === userStep,
        //   validAnswersCount: validAnswers.length,
        // })

        // set({
        //   userId,
        //   userStep, // ✅ STEP 저장
        //   wrongQuestions: questions,
        //   currentQuestionIndex: 0,
        //   userAnswers: validAnswers, // ✅ 조건부로 답변 유지
        //   showModal: false,
        //   modalData: null,
        // })
      },

      reset: () => {
        set({
          userId: null,
          userStep: null, // ✅ STEP도 초기화
          currentQuestionIndex: 0,
          wrongQuestions: [],
          userAnswers: [],
          showModal: false,
          modalData: null,
        })
      },

      // 퀴즈 진행
      goToNextQuestion: () => {
        const { currentQuestionIndex, wrongQuestions } = get()
        if (currentQuestionIndex < wrongQuestions.length - 1) {
          set({ currentQuestionIndex: currentQuestionIndex + 1 })
        }
      },

      goToPreviousQuestion: () => {
        const { currentQuestionIndex } = get()
        if (currentQuestionIndex > 0) {
          set({ currentQuestionIndex: currentQuestionIndex - 1 })
        }
      },

      setCurrentQuestionIndex: (index) => {
        const { wrongQuestions } = get()
        if (index >= 0 && index < wrongQuestions.length) {
          set({ currentQuestionIndex: index })
        }
      },

      submitAnswer: (questionId, answerIndex, isCorrect) => {
        const { userAnswers } = get()

        // 이미 답한 문제인지 확인
        const existingAnswerIndex = userAnswers.findIndex(
          (answer) => answer.questionId === questionId
        )

        const newAnswer: WrongUserAnswer = {
          questionId,
          selectedAnswerIndex: answerIndex,
          isCorrect,
          timestamp: new Date(),
        }

        let newUserAnswers: WrongUserAnswer[]
        if (existingAnswerIndex >= 0) {
          // 기존 답안 업데이트
          newUserAnswers = [...userAnswers]
          newUserAnswers[existingAnswerIndex] = newAnswer
        } else {
          // 새 답안 추가
          newUserAnswers = [...userAnswers, newAnswer]
        }

        set({ userAnswers: newUserAnswers })
      },

      // 모달 관리
      showAnswerModal: (data) => {
        set({ showModal: true, modalData: data })
      },

      closeModal: () => {
        set({ showModal: false, modalData: null })
      },

      // 데이터 조회
      getCurrentQuestion: () => {
        const { wrongQuestions, currentQuestionIndex } = get()
        return wrongQuestions[currentQuestionIndex] || null
      },

      isLastQuestion: () => {
        const { wrongQuestions, currentQuestionIndex } = get()
        return currentQuestionIndex === wrongQuestions.length - 1
      },

      getUserAnswers: () => {
        return get().userAnswers
      },
    }),
    {
      name: 'wrong-quiz-storage',
      partialize: (state) => ({
        userId: state.userId,
        userStep: state.userStep,
        wrongQuestions: state.wrongQuestions,
        userAnswers: state.userAnswers,
        currentQuestionIndex: state.currentQuestionIndex,
      }),
    }
  )
)
